import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';
import { Link } from 'react-router-dom';

export const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'how-it-works', label: 'How it works' },
  { id: 'one-per-scene', label: 'One result per scene' },
  { id: 'by-image', label: 'Search by image or by a frame' },
  { id: 'what-it-means', label: 'What this means for you' },
];

export default function Body() {
  return (
    <>
      <DocSection id="overview" title="Overview">
        <p>
          Type what you remember seeing — "wide shot of nature," "someone laughing," "a hand holding a
          coffee cup" — and <span style={kbd}>Visuals</span> mode finds it, even if no one ever tagged or
          captioned that clip. It's semantic search over the pixels themselves, not a keyword or tag
          lookup.
        </p>
        <DocShot slug="visual-search" shot="results" alt="Visuals-mode results block" caption="A Visuals search returns one card per matching scene, ranked by how well it matches." />
      </DocSection>
      <DocSection id="how-it-works" title="How it works">
        <p>
          During analysis, ClipRadar's visual model — OpenCLIP{' '}
          <span style={kbd}>ViT-SO400M-16-SigLIP2-512</span> — reads every frame and turns what it sees into a numeric embedding. Your search text is turned
          into an embedding from the same model, in the same space. ClipRadar then compares your query
          against every stored embedding directly — an exact in-RAM cosine similarity, not an approximate
          nearest-neighbor index — so the ranking you get back is exact, not a
          "close enough" guess.
        </p>
      </DocSection>
      <DocSection id="one-per-scene" title="One result per scene">
        <p>
          Visual analysis merges shot cuts into semantic scenes ahead of time, so a search returns one
          card per matching scene rather than one per raw frame or one giant card per file. See{' '}
          <Link to="/docs/concepts/analyzing-media">Analyzing your footage</Link> for how scenes are built
          during the Visual pass.
        </p>
      </DocSection>
      <DocSection id="by-image" title="Search by image or by a frame">
        <p>
          Don't have the words for it? Right-click a result and choose <span style={kbd}>Match similar</span> to
          find more of the same look. In the Premiere panel, you can also search from a picture directly:
          the <strong>Camera</strong> button grabs the frame currently on your Program Monitor, or paste
          an image straight into the search box with <span style={kbd}>Ctrl+V</span>.
        </p>
        <DocShot slug="visual-search" shot="by-image" alt="A search-by-image / match-similar example" caption="Match similar (or a pasted/captured frame) searches by picture instead of by words." />
      </DocSection>
      <DocSection id="what-it-means" title="What this means for you">
        <p>
          Because it reads pixels instead of tags, visual search finds footage you never labeled — B-roll
          nobody logged, an old project you barely remember shooting. It only sees what's visually there,
          though: it doesn't know what was said or who's in frame by name — for that, see{' '}
          <Link to="/docs/concepts/speech-search">Speech search</Link> and{' '}
          <Link to="/docs/concepts/people-and-faces">People &amp; faces</Link>. And because everything was
          embedded during analysis, results come back instantly, offline, on your own machine.
        </p>
      </DocSection>
    </>
  );
}
